import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, UtensilsCrossed } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button, cx, ErrorBanner, Field, Select, Textarea, TextField } from '../../components/ui';
import { AuthLayout } from './Auth';

type Step = 0 | 1 | 2 | 3;
const STEPS = ['Account type', 'Company', 'Trading details', 'Your login'];

export function RegisterPage() {
  const [step, setStep] = useState<Step>(0);
  const [orgType, setOrgType] = useState<'BUYER' | 'SUPPLIER'>('BUYER');
  const [company, setCompany] = useState({ legalName: '', displayName: '', registrationNumber: '', taxNumber: '', phone: '', website: '', country: 'EG', city: '', description: '', defaultCurrency: 'EGP' });
  const [supplier, setSupplier] = useState({ businessCategories: '', deliveryFee: '0', freeDeliveryThreshold: '', minOrderValue: '0', leadTimeDays: '2', serviceAreas: '' });
  const [buyer, setBuyer] = useState({ businessType: 'RESTAURANT', outletsCount: '1' });
  const [user, setUser] = useState({ fullName: '', email: '', password: '', phone: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const fe = error instanceof ApiError ? error.fieldErrors : {};
  const c = (k: keyof typeof company) => (v: string) => setCompany((x) => ({ ...x, [k]: v }));

  const canNext = [true, company.legalName.length > 1 && company.displayName.length > 1 && company.city && company.country.length === 2, true, user.fullName && user.email && user.password.length >= 10][step];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/register', {
        orgType,
        company: Object.fromEntries(Object.entries(company).filter(([, v]) => v !== '')),
        supplierProfile:
          orgType === 'SUPPLIER'
            ? {
                businessCategories: supplier.businessCategories.split(',').map((s) => s.trim()).filter(Boolean),
                deliveryFee: supplier.deliveryFee || '0',
                freeDeliveryThreshold: supplier.freeDeliveryThreshold || null,
                minOrderValue: supplier.minOrderValue || '0',
                leadTimeDays: Number(supplier.leadTimeDays || 2),
                serviceAreas: supplier.serviceAreas || undefined,
              }
            : undefined,
        buyerProfile: orgType === 'BUYER' ? { businessType: buyer.businessType, outletsCount: Number(buyer.outletsCount || 1) } : undefined,
        user: { ...user, phone: user.phone || undefined },
      });
      await refresh();
      nav('/onboarding', { replace: true });
    } catch (e) {
      setError(e);
      const fields = e instanceof ApiError ? Object.keys(e.fieldErrors) : [];
      if (fields.some((f) => f.startsWith('company'))) setStep(1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="cond text-[28px] font-semibold">Register your company</h1>
      <p className="mt-1 text-ink-2">Approval usually takes one business day. You can prepare your account in the meantime.</p>
      <ol className="mt-6 flex gap-1" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex-1">
            <div className={cx('h-1 rounded-full', i <= step ? 'bg-basil' : 'bg-line')} />
            <div className={cx('mt-1.5 text-[12px]', i === step ? 'font-medium text-ink' : 'text-ink-3')}>{s}</div>
          </li>
        ))}
      </ol>

      <div className="mt-6 space-y-4">
        {step === 0 && (
          <div className="grid gap-3">
            {(
              [
                ['BUYER', UtensilsCrossed, 'We buy supplies', 'Hotels, restaurants, cafés, caterers'],
                ['SUPPLIER', Store, 'We sell to hospitality', 'Distributors, wholesalers, producers'],
              ] as const
            ).map(([v, Icon, t, d]) => (
              <button
                key={v}
                type="button"
                onClick={() => setOrgType(v)}
                className={cx('flex items-center gap-3 rounded-lg border p-4 text-start', orgType === v ? 'border-basil bg-basil-tint/60 ring-1 ring-basil' : 'border-line-strong bg-surface hover:border-ink-3')}
                aria-pressed={orgType === v}
              >
                <Icon className="size-6 text-basil" aria-hidden />
                <span>
                  <span className="block font-medium">{t}</span>
                  <span className="block text-[13px] text-ink-3">{d}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField className="sm:col-span-2" label="Legal name" value={company.legalName} onChange={c('legalName')} error={fe['company.legalName']} required />
            <TextField className="sm:col-span-2" label="Trading name" hint="Shown to buyers and suppliers" value={company.displayName} onChange={c('displayName')} required />
            <TextField label="Commercial registration no." value={company.registrationNumber} onChange={c('registrationNumber')} />
            <TextField label="Tax registration no." value={company.taxNumber} onChange={c('taxNumber')} />
            <TextField label="Country code" hint="Two letters, e.g. EG, SA, AE" maxLength={2} value={company.country} onChange={(v) => c('country')(v.toUpperCase())} required />
            <TextField label="City" value={company.city} onChange={c('city')} required />
            <TextField label="Phone" value={company.phone} onChange={c('phone')} />
            <Field label="Trading currency">
              {(id) => (
                <Select id={id} value={company.defaultCurrency} onChange={(e) => c('defaultCurrency')(e.target.value)}>
                  {['EGP', 'SAR', 'AED', 'USD'].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        )}
        {step === 2 && orgType === 'SUPPLIER' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField className="sm:col-span-2" label="What do you sell?" hint="Comma-separated, e.g. Coffee, Tea" value={supplier.businessCategories} onChange={(v) => setSupplier((s) => ({ ...s, businessCategories: v }))} />
            <TextField label="Delivery fee per order" inputMode="decimal" value={supplier.deliveryFee} onChange={(v) => setSupplier((s) => ({ ...s, deliveryFee: v }))} />
            <TextField label="Free delivery from" hint="Leave empty for never" inputMode="decimal" value={supplier.freeDeliveryThreshold} onChange={(v) => setSupplier((s) => ({ ...s, freeDeliveryThreshold: v }))} />
            <TextField label="Minimum order value" inputMode="decimal" value={supplier.minOrderValue} onChange={(v) => setSupplier((s) => ({ ...s, minOrderValue: v }))} />
            <TextField label="Lead time (days)" inputMode="numeric" value={supplier.leadTimeDays} onChange={(v) => setSupplier((s) => ({ ...s, leadTimeDays: v }))} />
            <Field label="Areas you deliver to" className="sm:col-span-2">
              {(id) => <Textarea id={id} value={supplier.serviceAreas} onChange={(e) => setSupplier((s) => ({ ...s, serviceAreas: e.target.value }))} />}
            </Field>
          </div>
        )}
        {step === 2 && orgType === 'BUYER' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type of business">
              {(id) => (
                <Select id={id} value={buyer.businessType} onChange={(e) => setBuyer((b) => ({ ...b, businessType: e.target.value }))}>
                  <option value="HOTEL">Hotel</option>
                  <option value="RESTAURANT">Restaurant</option>
                  <option value="CAFE">Café</option>
                  <option value="CATERING">Catering</option>
                  <option value="OTHER">Other hospitality</option>
                </Select>
              )}
            </Field>
            <TextField label="Number of outlets" inputMode="numeric" value={buyer.outletsCount} onChange={(v) => setBuyer((b) => ({ ...b, outletsCount: v }))} />
          </div>
        )}
        {step === 3 && (
          <div className="grid gap-4">
            <p className="text-[13.5px] text-ink-2">You will be the administrator. You can invite colleagues once you're in.</p>
            <TextField label="Full name" value={user.fullName} onChange={(v) => setUser((u) => ({ ...u, fullName: v }))} required />
            <TextField label="Work email" type="email" autoComplete="username" value={user.email} onChange={(v) => setUser((u) => ({ ...u, email: v }))} error={fe['user.email']} required />
            <TextField label="Password" type="password" autoComplete="new-password" hint="At least 10 characters with letters and numbers" value={user.password} onChange={(v) => setUser((u) => ({ ...u, password: v }))} error={fe['user.password']} required />
            <TextField label="Mobile (optional)" value={user.phone} onChange={(v) => setUser((u) => ({ ...u, phone: v }))} />
          </div>
        )}
        <ErrorBanner error={error} />
        <div className="flex justify-between pt-2">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep((s) => (s - 1) as Step)}>
              Back
            </Button>
          ) : (
            <Link to="/login" className="self-center text-[13.5px] text-basil hover:underline">
              I already have an account
            </Link>
          )}
          {step < 3 ? (
            <Button variant="primary" disabled={!canNext} onClick={() => setStep((s) => (s + 1) as Step)}>
              Continue
            </Button>
          ) : (
            <Button variant="primary" disabled={!canNext} loading={busy} onClick={submit}>
              Create account
            </Button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
